import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  AlertTriangle,
  Bell,
  Users,
  QrCode,
  Camera,
  ClipboardCheck,
  History,
  FolderOpen,
  Filter,
  ShieldCheck,
  Download,
  Sparkles,
  Check,
  Star,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import LeadForm from '@/components/LeadForm';
import { useLanguage } from '@/contexts/LanguageContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Landing() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-app overflow-x-hidden" data-testid="landing-page">
      <Navigation />

      {/* ============== HERO ============== */}
      <section className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/37719129/pexels-photo-37719129.jpeg"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F9F8F6] via-[#F9F8F6]/95 to-[#F9F8F6]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#F9F8F6] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <motion.span
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="label-eyebrow inline-block mb-5"
              data-testid="hero-eyebrow"
            >
              {t.hero.eyebrow}
            </motion.span>
            <motion.h1
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={1}
              className="font-serif text-5xl md:text-7xl leading-[1.05] tracking-tight text-app mb-6"
              data-testid="hero-title"
            >
              {t.hero.title1}
              <br />
              <span className="text-primary-app italic">{t.hero.title2}</span>
            </motion.h1>
            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={2}
              className="text-lg md:text-xl text-app-secondary leading-relaxed mb-9 max-w-2xl"
              data-testid="hero-subtitle"
            >
              {t.hero.subtitle}
            </motion.p>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={3}
              className="flex flex-col sm:flex-row gap-3"
            >
              <a
                href="#contacto"
                className="inline-flex items-center justify-center gap-2 bg-primary-app text-white font-medium px-7 py-3.5 rounded-md hover:bg-[#8F3E2E] transition-colors shadow-sm"
                data-testid="hero-cta-primary"
              >
                {t.hero.ctaPrimary} <ArrowRight size={18} />
              </a>
              <Link
                to="/demo"
                className="inline-flex items-center justify-center gap-2 bg-white border border-app text-app font-medium px-7 py-3.5 rounded-md hover:bg-surface-muted transition-colors"
                data-testid="hero-cta-secondary"
              >
                {t.hero.ctaSecondary}
              </Link>
            </motion.div>

            {/* mini stats strip */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={4}
              className="mt-16 grid grid-cols-3 gap-6 max-w-2xl border-t border-app pt-8"
            >
              <Stat n="68%" l={t.hero.stat1} />
              <Stat n="-40%" l={t.hero.stat2} />
              <Stat n="3×" l={t.hero.stat3} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============== PROBLEM ============== */}
      <section id="producto" className="py-24 md:py-32 bg-alt border-y border-app">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <motion.span
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="label-eyebrow block mb-4"
          >
            {t.problem.eyebrow}
          </motion.span>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={1}
            className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-app mb-8 max-w-3xl"
            data-testid="problem-title"
          >
            {t.problem.title}
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={2}
            className="text-lg text-app-secondary leading-relaxed mb-12 max-w-3xl"
          >
            {t.problem.body}
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {t.problem.points.map((p, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i + 3}
                className="flex items-start gap-3 bg-surface border border-app rounded-md p-5"
                data-testid={`problem-point-${i}`}
              >
                <AlertTriangle className="text-primary-app flex-shrink-0 mt-0.5" size={18} />
                <span className="text-sm text-app-secondary">{p}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== SOLUTION — 3 Pillars ============== */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <span className="label-eyebrow block mb-4">{t.solution.eyebrow}</span>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-app mb-4">
              {t.solution.title}
            </h2>
            <p className="text-lg text-app-secondary">{t.solution.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: AlertTriangle, color: '#B34E3A', bg: '#B34E3A' },
              { icon: Bell, color: '#5B6E56', bg: '#5B6E56' },
              { icon: Users, color: '#2B6CB0', bg: '#2B6CB0' },
            ].map((cfg, i) => {
              const Icon = cfg.icon;
              const pillar = t.solution.pillars[i];
              return (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="bg-surface border border-app rounded-lg p-8 hover:-translate-y-1 transition-transform duration-300 shadow-sm"
                  data-testid={`pillar-${i}`}
                >
                  <div
                    className="w-12 h-12 rounded-md flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${cfg.bg}14` }}
                  >
                    <Icon size={22} style={{ color: cfg.color }} strokeWidth={2} />
                  </div>
                  <h3 className="font-serif text-2xl text-app mb-3">{pillar.title}</h3>
                  <p className="text-app-secondary leading-relaxed">{pillar.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section id="como-funciona" className="py-24 md:py-32 bg-alt border-y border-app">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="label-eyebrow block mb-4">{t.how.eyebrow}</span>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-app mb-12">
                {t.how.title}
              </h2>
              <div className="space-y-8">
                {t.how.steps.map((s, i) => (
                  <motion.div
                    key={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    custom={i}
                    className="flex gap-5"
                    data-testid={`step-${i}`}
                  >
                    <span className="font-serif text-3xl text-primary-app font-medium flex-shrink-0 w-12">
                      {s.n}
                    </span>
                    <div>
                      <h3 className="text-xl font-medium text-app mb-1.5">{s.t}</h3>
                      <p className="text-app-secondary">{s.d}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              <Link
                to="/demo"
                className="inline-flex items-center gap-2 mt-12 text-primary-app font-medium hover:gap-3 transition-all"
                data-testid="how-demo-link"
              >
                {t.nav.demo} <ArrowRight size={16} />
              </Link>
            </div>

            {/* Phone mockup */}
            <div className="relative flex justify-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="relative w-[280px] md:w-[320px] bg-app border-[10px] border-[#1A1F2B] rounded-[42px] shadow-2xl overflow-hidden"
                data-testid="phone-mockup"
              >
                <div className="bg-[#1A1F2B] h-6 flex items-center justify-center">
                  <div className="w-20 h-3 bg-black rounded-full" />
                </div>
                <div className="bg-app p-5">
                  <div className="text-center mb-4">
                    <div className="text-xs label-eyebrow mb-1">Edificio · C/ Mar 14</div>
                    <div className="font-serif text-xl text-app">Reportar incidencia</div>
                  </div>
                  <div className="qr-mock w-32 h-32 mx-auto rounded-md mb-5" />
                  <div className="space-y-2.5">
                    <div className="bg-white border border-app rounded-md px-3 py-2.5 text-xs text-app-muted">
                      Vivienda
                    </div>
                    <div className="bg-white border border-app rounded-md px-3 py-2.5 text-xs text-app-muted">
                      Categoría
                    </div>
                    <div className="bg-white border border-app rounded-md px-3 py-2.5 text-xs text-app-muted h-16">
                      Descripción…
                    </div>
                    <div className="bg-primary-app text-white text-center text-xs py-2.5 rounded-md font-medium">
                      Enviar
                    </div>
                  </div>
                </div>
              </motion.div>
              {/* decorative elements */}
              <div className="absolute -top-6 -right-6 w-28 h-28 bg-primary-app/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-6 -left-6 w-28 h-28 bg-secondary-app/10 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ============== FEATURES ============== */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <span className="label-eyebrow block mb-4">{t.features.eyebrow}</span>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-app">
              {t.features.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[History, FolderOpen, Filter, ShieldCheck, Download, Sparkles].map((Icon, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i % 3}
                className="border-t border-app pt-6"
                data-testid={`feature-${i}`}
              >
                <Icon size={20} className="text-primary-app mb-4" />
                <h3 className="text-lg font-medium text-app mb-2">{t.features.items[i].t}</h3>
                <p className="text-sm text-app-secondary leading-relaxed">
                  {t.features.items[i].d}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== TARGET AUDIENCE ============== */}
      <section className="py-20 bg-[#1A1F2B] text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <span className="label-eyebrow text-[#D4A373] block mb-4">{t.target.eyebrow}</span>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight mb-6">
            {t.target.title}
          </h2>
          <p className="text-lg text-white/70 max-w-3xl mx-auto">{t.target.body}</p>
        </div>
      </section>

      {/* ============== PRICING ============== */}
      <section id="precios" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <span className="label-eyebrow block mb-4">{t.pricing.eyebrow}</span>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-app mb-4">
              {t.pricing.title}
            </h2>
            <p className="text-lg text-app-secondary">{t.pricing.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.pricing.plans.map((plan, i) => {
              const featured = i === 1;
              return (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className={`relative rounded-lg p-8 border transition-transform hover:-translate-y-1 ${
                    featured
                      ? 'bg-[#1A1F2B] text-white border-[#1A1F2B] shadow-xl'
                      : 'bg-surface border-app shadow-sm'
                  }`}
                  data-testid={`pricing-plan-${i}`}
                >
                  {featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-app text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                      <Star size={12} /> {t.pricing.mostPopular}
                    </div>
                  )}
                  <div className="mb-6">
                    <div
                      className={`text-sm font-medium mb-3 ${
                        featured ? 'text-[#D4A373]' : 'text-primary-app'
                      }`}
                    >
                      {plan.name}
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className={`font-serif text-5xl ${featured ? 'text-white' : 'text-app'}`}>
                        {plan.price}
                      </span>
                      <span className={featured ? 'text-white/60' : 'text-app-muted'}>{plan.unit}</span>
                    </div>
                    <div className={`text-sm mt-2 ${featured ? 'text-white/70' : 'text-app-secondary'}`}>
                      {plan.desc}
                    </div>
                    <div className={`text-xs mt-1 ${featured ? 'text-white/50' : 'text-app-muted'}`}>
                      {plan.extra}
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feat, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm">
                        <Check
                          size={16}
                          className={`mt-0.5 flex-shrink-0 ${
                            featured ? 'text-[#D4A373]' : 'text-[#3B7A57]'
                          }`}
                        />
                        <span className={featured ? 'text-white/85' : 'text-app-secondary'}>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#contacto"
                    className={`block text-center font-medium px-5 py-3 rounded-md transition-colors ${
                      featured
                        ? 'bg-primary-app text-white hover:bg-[#8F3E2E]'
                        : 'bg-app border border-app text-app hover:bg-surface-muted'
                    }`}
                    data-testid={`pricing-cta-${i}`}
                  >
                    {t.pricing.cta}
                  </a>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== FAQ ============== */}
      <section id="faq" className="py-24 md:py-32 bg-alt border-y border-app">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <span className="label-eyebrow block mb-4">{t.faq.eyebrow}</span>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-app mb-12">
            {t.faq.title}
          </h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {t.faq.items.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="bg-surface border border-app rounded-md px-5 data-[state=open]:shadow-sm"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-base font-medium text-app py-5 hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-app-secondary leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============== LEAD CAPTURE ============== */}
      <section id="contacto" className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="label-eyebrow block mb-4">{t.leadForm.eyebrow}</span>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-app mb-4">
              {t.leadForm.title}
            </h2>
            <p className="text-lg text-app-secondary max-w-2xl mx-auto">{t.leadForm.subtitle}</p>
          </div>
          <LeadForm />
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="py-20 bg-primary-app text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mb-4">
            {t.finalCta.title}
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">{t.finalCta.subtitle}</p>
          <a
            href="#contacto"
            className="inline-flex items-center gap-2 bg-white text-primary-app font-medium px-7 py-3.5 rounded-md hover:bg-app transition-colors"
            data-testid="final-cta"
          >
            {t.finalCta.cta} <ArrowRight size={18} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-serif text-3xl md:text-4xl text-app mb-1">{n}</div>
      <div className="text-xs text-app-muted leading-snug max-w-[10rem]">{l}</div>
    </div>
  );
}
